
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Map } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface MapErrorDisplayProps {
  language: string;
  errorType: 'missing-key' | 'load-error';
}

const MapErrorDisplay = ({ language, errorType }: MapErrorDisplayProps) => {
  const navigate = useNavigate();
  
  if (errorType === 'missing-key') {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-50 p-6 text-center">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4 w-full max-w-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                {language === 'en' 
                  ? 'Please replace the Google Maps API key in OutdoorMap.tsx with your actual API key.' 
                  : 'יש להחליף את מפתח ה-API של Google Maps ב-OutdoorMap.tsx במפתח האמיתי שלך.'}
              </p>
            </div>
          </div>
        </div>
        <p className="text-gray-600 mb-4">
          {language === 'en' 
            ? 'The outdoor map requires a Google Maps API key to display properly.' 
            : 'המפה החיצונית דורשת מפתח API של Google Maps כדי להציג כראוי.'}
        </p>
        <div className="bg-white p-4 rounded-md shadow w-full max-w-md">
          <code className="text-xs break-all block p-2 bg-gray-100 rounded">
            {`// In src/components/maps/OutdoorMap.tsx
const googleMapsApiKey = "YOUR_GOOGLE_MAPS_API_KEY";`}
          </code>
        </div>
      </div>
    );
  }
  
  // Show error message if map fails to load
  return (
    <div className="flex flex-col items-center justify-center h-full bg-gray-50 p-6 text-center">
      <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4 w-full max-w-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">
              {language === 'en' 
                ? 'Google Maps failed to load correctly.' 
                : 'Google Maps לא נטען כראוי.'}
            </p>
          </div>
        </div>
      </div>
      <div className="mb-6">
        <Map className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-600 mb-2">
          {language === 'en' 
            ? 'This could be due to:' 
            : 'זה יכול להיות בגלל:'}
        </p>
        <ul className="text-gray-600 text-sm list-disc text-left ml-8 mb-4">
          <li>
            {language === 'en'
              ? 'The API key is not authorized for this domain'
              : 'מפתח ה-API אינו מורשה עבור דומיין זה'}
          </li>
          <li>
            {language === 'en'
              ? 'A network connection issue'
              : 'בעיית חיבור לרשת'}
          </li>
        </ul>
        <p className="text-gray-600 text-sm">
          {language === 'en'
            ? 'For production use, you should replace the demo API key with your own Google Maps API key.'
            : 'לשימוש בייצור, עליך להחליף את מפתח ה-API לדוגמה במפתח ה-API של Google Maps שלך.'}
        </p>
      </div>
      <Button 
        variant="outline" 
        className="px-6 mt-2" 
        onClick={() => navigate(-1)}
      >
        {language === 'en' ? 'Go Back' : 'חזור'}
      </Button>
    </div>
  );
};

export default MapErrorDisplay;
